import React from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Plus, Trash } from 'phosphor-react';
import { Controller, get, useFormContext, useFormState, useWatch } from 'react-hook-form';

import {
  AcaoSocio,
  IDestinoQuotaForm,
  ISocioFormValues,
  IWizardFormValues,
  TipoAlteracaoSocio,
} from '../schema';
import {
  aspectoLiberado,
  disponivelParaCeder,
  liberacoesDoPassoSocios,
  somaCedida,
} from '../regrasDoPassoSocios';

// Sessao 2026-05-11 (item 15 Fase 4): sub-aspectos do `acao=alterar`.
const TIPOS_ALTERACAO_SOCIO_OPTIONS: Array<{
  value: TipoAlteracaoSocio;
  label: string;
  helper: string;
}> = [
  { value: 'quotas', label: 'Quotas', helper: 'Mudou a quantidade' },
  { value: 'endereco', label: 'Endereço', helper: 'Mudou de residência' },
  { value: 'nome', label: 'Nome', helper: 'Geralmente por casamento' },
  { value: 'profissao', label: 'Profissão', helper: 'Mudou de ocupação' },
  {
    value: 'vinculo',
    label: 'Vínculo',
    helper: 'Passou a administrar ou deixou de administrar — ajuste o vínculo no campo do sócio.',
  },
  { value: 'estado_civil', label: 'Estado civil', helper: 'Casou/divorciou — afeta regime' },
];

// Aspectos aplicaveis a uma socia PJ (Porta 3, 2026-06-18): o v3 reescreve
// quotas (via quadro), razao social (nome) e sede (endereco) de socia PJ.
// profissao/estado_civil/vinculo nao se aplicam a PJ; mudar quem administra =
// tipo "Administracao". Labels proprios (razao social / sede em vez do
// fraseado PF "nome por casamento" / "mudou de residencia").
const TIPOS_ALTERACAO_SOCIO_OPTIONS_PJ: Array<{
  value: TipoAlteracaoSocio;
  label: string;
  helper: string;
}> = [
  { value: 'quotas', label: 'Quotas', helper: 'Mudou a quantidade' },
  { value: 'nome', label: 'Razão social', helper: 'Mudou o nome empresarial' },
  { value: 'endereco', label: 'Sede', helper: 'Mudou o endereço da sede' },
  // 2026-07-16: opção 'representante' (editar-leve/trocar o representante legal)
  // SHELVED — a base backend+front está pronta (cirúrgico de endereço do rep,
  // rebuild "trocar a pessoa", payload nested, sub-ficha) mas o valor não foi
  // confirmado. Reintroduzir aqui + escolher o escopo (trocar-a-pessoa vs
  // editar-campos) quando houver necessidade real. O backend ignora
  // 'representante' sem bloquear (validador + gate benignos).
];

/**
 * Sessao 2026-05-11 (socios sync) — radio de acao + sub-form de
 * redistribuicao inline (Alt A com salvaguardas).
 *
 * - Aparece dentro do card do socio quando o wizard estiver em modo
 *   socios sync (alteracao com quadro_societario marcado).
 * - acao=sair colapsa visualmente o resto do card (no StepSocios) e
 *   abre o sub-form de redistribuicao aqui mesmo.
 * - Soma de quotas redistribuidas exibida em tempo real com barra
 *   verde/vermelha — bate localmente. Backend faz a checagem final
 *   contra o vigente extraido (V_REDISTRIBUICAO_QUOTAS_BATE_VIGENTE).
 */
interface AcaoSocioBlockProps {
  /** Indice do socio no useFieldArray. */
  index: number;
  /** Lista de ordens dos demais socios (remanescentes) — alvos validos
   *  de redistribuicao. Filtrado pelo StepSocios. */
  ordensDestino: { ordem: number; label: string }[];
  /** Quotas vigentes deste socio (do quadro_socios_atual do extrator).
   *  Usada pra mostrar barra de progresso da redistribuicao. Quando
   *  null, exibe so a soma sem comparacao. */
  quotasVigente: number | null;
  /** Quotas que este socio recebe no mesmo ato: somam ao que ele pode ceder. */
  quotasRecebidas?: number;
  /** Destinos de cessao que nao valem mais (sairam do quadro), por ordem: o aviso da linha. */
  destinosInvalidos?: Record<number, string>;
  /** Leitura sem o quadro: nenhum card tem par, e todos escolhem entre todas as acoes. */
  leituraSemQuadro?: boolean;
}

const SEM_DESTINOS_INVALIDOS: Record<number, string> = {};

const ACAO_LABELS: Record<AcaoSocio, { label: string; helper: string; color: string }> = {
  manter: { label: 'Manter', helper: 'Sócio fica como está', color: 'gray' },
  alterar: { label: 'Alterar', helper: 'Sócio permanece com mudanças', color: 'blue' },
  sair: { label: 'Sair', helper: 'Sócio se retira; redistribua as quotas', color: 'red' },
  adicionar: { label: 'Adicionar', helper: 'Sócio novo entrando', color: 'green' },
};

// Quem vem do vigente escolhe entre manter, alterar e sair; quem entra nasce "adicionar"
// pelo botão próprio. "Adicionar" no card do vigente tirava a ficha dele da leitura.
const ACOES_DO_VIGENTE: AcaoSocio[] = ['manter', 'alterar', 'sair'];
const ACOES_DE_QUEM_ENTRA: AcaoSocio[] = ['adicionar'];
const TODAS_AS_ACOES: AcaoSocio[] = [...ACOES_DO_VIGENTE, ...ACOES_DE_QUEM_ENTRA];

// Campos que cada aspecto libera. Desmarcar o aspecto (ou sair de "Alterar") devolve o valor
// de antes: senao o digitado ficava gravado sem ato declarado. Quotas tem o proprio efeito.
const CAMPOS_DO_ASPECTO: Partial<Record<TipoAlteracaoSocio, string[]>> = {
  quotas: ['redistribuicao_quotas'],
  nome: ['nome'],
  profissao: ['profissao'],
  vinculo: ['tipo_vinculo'],
  endereco: ['endereco_estruturado'],
  estado_civil: [
    'estado_civil',
    'regime_casamento',
    'pacto_antenupcial',
    'nome_conjuge',
    'cpf_conjuge',
  ],
};

export const AcaoSocioBlock: React.FC<AcaoSocioBlockProps> = ({
  index,
  ordensDestino,
  quotasVigente,
  quotasRecebidas = 0,
  destinosInvalidos = SEM_DESTINOS_INVALIDOS,
  leituraSemQuadro = false,
}) => {
  const { control, setValue, getValues } = useFormContext<IWizardFormValues>();
  // Erros que o Continuar marca nas linhas da cessao (ex.: socios.0.redistribuicao_quotas.0.qtd).
  const { errors } = useFormState({ control, name: `socios.${index}.redistribuicao_quotas` });
  const antesDoAspecto = React.useRef<Record<string, unknown>>({});
  const aspectosMarcados = (useWatch({
    control,
    name: `socios.${index}.tipos_alteracao_socio`,
  }) ?? []) as TipoAlteracaoSocio[];
  const acao = useWatch({
    control,
    name: `socios.${index}.acao`,
  }) as AcaoSocio | null | undefined;

  // Alteração de CAPITAL (2026-07-15): quando o capital muda, o operador ajusta
  // as quotas dos sócios que PERMANECEM (acao=manter). O helper padrão do
  // "Manter" ("Sócio fica como está") confunde nesse caso — deixa claro que
  // manter o vínculo E ajustar as quotas é o esperado.
  const tiposAlteracao = (useWatch({ control, name: 'tipos_alteracao' }) ?? []) as string[];
  const capitalAlterado = tiposAlteracao.includes('capital');
  // O que os tipos marcados liberam no card (tabela de liberacao, 25/09): Sair/entrar so' com
  // Quadro ou Administracao; "Cede quotas a" so' com Quadro.
  const liberacoes = liberacoesDoPassoSocios(tiposAlteracao);
  const nomeDoSocio = useWatch({ control, name: `socios.${index}.nome` }) as string | undefined;

  const redistribuicao = useWatch({
    control,
    name: `socios.${index}.redistribuicao_quotas`,
  }) as IDestinoQuotaForm[] | null | undefined;

  // Porta 3 (2026-06-18): o v3 reescreve quotas/razao social/sede de socia PJ.
  // Pra PJ oferecemos esses 3 aspectos (com labels proprios); profissao/estado
  // civil/vinculo nao se aplicam. Mudar quem administra = tipo "Administracao".
  const tipoPessoa = useWatch({
    control,
    name: `socios.${index}.tipo_pessoa`,
  }) as ISocioFormValues['tipo_pessoa'] | undefined;
  const ehPj = tipoPessoa === 'pj';
  const opcoesAspecto = ehPj ? TIPOS_ALTERACAO_SOCIO_OPTIONS_PJ : TIPOS_ALTERACAO_SOCIO_OPTIONS;

  // Porta 2 Wave 1 (2026-06-18): so quem e/era administrador pode "permanecer
  // como administrador" ao sair (um socio comum nao administrava). Gate do
  // checkbox no bloco de saida.
  const tipoVinculo = useWatch({
    control,
    name: `socios.${index}.tipo_vinculo`,
  }) as ISocioFormValues['tipo_vinculo'] | undefined;
  const eraAdministrador = tipoVinculo === 'socio_administrador' || tipoVinculo === 'administrador';

  const guardarOuDevolver = (marcados: TipoAlteracaoSocio[], desmarcados: TipoAlteracaoSocio[]) => {
    marcados.forEach(asp =>
      (CAMPOS_DO_ASPECTO[asp] ?? []).forEach(campo => {
        const valor = getValues(`socios.${index}.${campo}` as any);
        antesDoAspecto.current[campo] = valor == null ? valor : JSON.parse(JSON.stringify(valor));
      }),
    );
    desmarcados.forEach(asp =>
      (CAMPOS_DO_ASPECTO[asp] ?? []).forEach(campo => {
        if (!(campo in antesDoAspecto.current)) return;
        setValue(`socios.${index}.${campo}` as any, antesDoAspecto.current[campo] as any, {
          shouldDirty: true,
          shouldValidate: true,
        });
        delete antesDoAspecto.current[campo];
      }),
    );
  };

  const handleAcaoChange = (nova: AcaoSocio) => {
    // Clicar de novo na acao marcada nao faz nada: "Alterar" outra vez zerava a cessao (28/09).
    if (nova === acao) return;
    if (acao === 'alterar' && nova !== 'alterar') {
      guardarOuDevolver([], (getValues(`socios.${index}.tipos_alteracao_socio`) ?? []) as any);
    }
    setValue(`socios.${index}.acao`, nova, { shouldDirty: true, shouldValidate: true });
    // tipos_alteracao_socio só faz sentido com acao=alterar (o backend valida
    // e devolve 400). Limpa o resíduo ao sair de 'alterar' — senão um sócio
    // que vira 'sair'/'manter'/'adicionar' carrega "quotas" etc (o campo some
    // da tela quando o card colapsa, o operador não consegue desmarcar).
    if (nova !== 'alterar') {
      setValue(`socios.${index}.tipos_alteracao_socio`, null, { shouldDirty: true });
    }
    // Limpa redistribuicao ao sair de 'sair'. Inicializa com 1 destino vazio
    // ao entrar em 'sair' pra UX (operador ve campos prontos pra preencher).
    if (nova === 'sair') {
      const atual = getValues(`socios.${index}.redistribuicao_quotas`);
      if (!atual || atual.length === 0) {
        setValue(`socios.${index}.redistribuicao_quotas`, [{ socio_ordem: null, qtd: null }], {
          shouldDirty: true,
        });
      }
    } else {
      setValue(`socios.${index}.redistribuicao_quotas`, null, { shouldDirty: true });
    }
    // Porta 2 Wave 1 (2026-06-18): o flag "permanece como administrador" so vale
    // em acao=sair. Limpa ao sair de 'sair' pra nao carregar residuo.
    if (nova !== 'sair') {
      setValue(`socios.${index}.permanece_como_administrador`, false, { shouldDirty: true });
    }
  };

  const adicionarDestino = () => {
    const atual = (getValues(`socios.${index}.redistribuicao_quotas`) ?? []) as IDestinoQuotaForm[];
    setValue(
      `socios.${index}.redistribuicao_quotas`,
      [...atual, { socio_ordem: null, qtd: null }],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  };

  const removerDestino = (idxDestino: number) => {
    const atual = (getValues(`socios.${index}.redistribuicao_quotas`) ?? []) as IDestinoQuotaForm[];
    const novo = atual.filter((_, i) => i !== idxDestino);
    setValue(`socios.${index}.redistribuicao_quotas`, novo.length > 0 ? novo : null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const soma = (redistribuicao ?? []).reduce((acc, d) => acc + (Number(d?.qtd) || 0), 0);
  // Sessao 2026-05-14 (review Matias PR #345 #7): comparacao com tolerancia.
  // Inputs forcam inteiro via parseInt, mas defesa-em-profundidade evita que
  // float introduzido por qualquer caminho (mock, retomada, decimal colado)
  // quebre o `===` exato e impeca o operador de avancar.
  const somaMatch = quotasVigente != null && Math.abs(soma - quotasVigente) < 0.5;
  const progresso =
    quotasVigente && quotasVigente > 0 ? Math.min(100, (soma / quotasVigente) * 100) : 0;

  // Cessao maior do que o socio tem (item 2, 25/09), com a conta do schema (28/09): quem sai cede
  // o vigente; quem altera, o vigente e o que recebe no mesmo ato. So' quantidades positivas.
  const quem = nomeDoSocio || `Sócio ${index + 1}`;
  let avisoExcesso: string | null = null;
  if (quotasVigente != null) {
    const disponivel = disponivelParaCeder(acao, quotasVigente, quotasRecebidas);
    const cedido = somaCedida({ redistribuicao_quotas: redistribuicao });
    if (cedido > disponivel) {
      const tem = disponivel.toLocaleString('pt-BR');
      avisoExcesso = `${quem} cede ${cedido.toLocaleString('pt-BR')} quotas, mas tem ${tem}.`;
    }
  }
  // Com o erro do Continuar na linha (campo das quotas), o aviso nao se repete embaixo.
  const erroNaLinha = (redistribuicao ?? []).some(
    (_, i) => !!get(errors, `socios.${index}.redistribuicao_quotas.${i}.qtd`),
  );

  // "Adicionar" so' no card sem acao, que so' existe quando a leitura nao trouxe o quadro (item 3,
  // 28/09): quem vem do vigente escolhe entre manter, alterar e sair, e quem entrou pelo botao
  // fica "Adicionar". Na leitura sem o quadro nenhum card tem par: todos escolhem entre todas,
  // e o "Adicionar" clicado no card padrao tem volta.
  let acoesDoCard: AcaoSocio[] = ACOES_DO_VIGENTE;
  if (acao == null || leituraSemQuadro) acoesDoCard = TODAS_AS_ACOES;
  else if (acao === 'adicionar') acoesDoCard = ACOES_DE_QUEM_ENTRA;
  // Sair e entrar so' com Quadro ou Administracao; a acao ja marcada continua visivel.
  const acoesOferecidas = acoesDoCard.filter(
    opt => opt === acao || liberacoes.entrarSair || (opt !== 'sair' && opt !== 'adicionar'),
  );

  // Aspecto oferecido: o que os tipos marcados liberam. "Vinculo" sempre que Quadro ou
  // Administracao libera, para declarar nomeacao ou destituicao (R3, 28/09). O aspecto ja marcado
  // continua visivel, para poder desmarcar.
  const aspectoOferecido = (aspecto: TipoAlteracaoSocio) =>
    aspectosMarcados.includes(aspecto) || aspectoLiberado(aspecto, liberacoes);

  // "Cede quotas a" so' com Quadro marcado; a cessao ja declarada fica visivel, para remover.
  const mostraCessao =
    acao === 'alterar' &&
    aspectosMarcados.includes('quotas') &&
    (liberacoes.cessao || (redistribuicao ?? []).length > 0);

  let textoDaAcao = acao ? ACAO_LABELS[acao].helper : '';
  if (acao === 'manter' && capitalAlterado) {
    textoDaAcao = 'Sócio permanece na sociedade — ajuste as quotas conforme o novo capital.';
  } else if (acao === 'manter' && tipoVinculo === 'administrador') {
    textoDaAcao = 'Continua como administrador';
  }

  // Linhas "para quem" (destino + quotas): a saida redistribui tudo; quem continua cede parte.
  const linhasDeDestino = (
    <Stack spacing={2}>
      {(redistribuicao ?? []).map((destino, idxDestino) => {
        const ordemEscolhida = destino?.socio_ordem;
        // Destino que saiu do quadro depois de escolhido (item 5, 25/09): a linha avisa e o que
        // o operador digitou fica; ele troca o destino.
        const motivoInvalido =
          ordemEscolhida != null ? destinosInvalidos[ordemEscolhida] : undefined;
        // Linha com quotas e sem destino (o destino foi removido do quadro): pede outro (28/09).
        // O erro do Continuar no mesmo campo, quando houver, ja' diz isso.
        const caminhoDoDestino = `socios.${index}.redistribuicao_quotas.${idxDestino}.socio_ordem`;
        const semDestino =
          ordemEscolhida == null && Number(destino?.qtd) > 0 && !get(errors, caminhoDoDestino);
        return (
          // useFieldArray seria mais elegante, mas exige nested o que
          // complica os tipos. Como o caso comum e 1-3 destinos, indexed
          // map + setValue funciona bem e mantem o codigo coeso.
          // eslint-disable-next-line react/no-array-index-key
          <Box key={idxDestino}>
            <Flex gap={2} align="flex-end">
              <Controller
                control={control}
                name={`socios.${index}.redistribuicao_quotas.${idxDestino}.socio_ordem`}
                render={({ field, fieldState }) => (
                  <FormControl
                    isInvalid={!!fieldState.error || !!motivoInvalido || semDestino}
                    flex={2}
                  >
                    <FormLabel fontSize="xs" mb={1}>
                      Destinatário
                    </FormLabel>
                    <Select
                      size="sm"
                      bg="white"
                      value={field.value ?? ''}
                      onChange={e =>
                        field.onChange(e.target.value === '' ? null : Number(e.target.value))
                      }
                      placeholder="Selecione um sócio"
                    >
                      {ordensDestino.map(o => (
                        <option key={o.ordem} value={o.ordem}>
                          {o.label}
                        </option>
                      ))}
                      {/* Quem saiu nao e' mais opcao, mas a escolha dele aparece. */}
                      {motivoInvalido && (
                        <option value={ordemEscolhida ?? ''} disabled>
                          {motivoInvalido}
                        </option>
                      )}
                    </Select>
                    {fieldState.error?.message && (
                      <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name={`socios.${index}.redistribuicao_quotas.${idxDestino}.qtd`}
                render={({ field, fieldState }) => (
                  <FormControl isInvalid={!!fieldState.error} flex={1}>
                    <FormLabel fontSize="xs" mb={1}>
                      Quotas
                    </FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={1}
                      step={1}
                      bg="white"
                      // Evita que o scroll do mouse altere o valor (bug nativo
                      // do input number). Blur ao rolar, igual ao InputForm.
                      onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                      value={field.value ?? ''}
                      onChange={e => {
                        const v = e.target.value;
                        if (v === '') {
                          field.onChange(null);
                          return;
                        }
                        // Sessao 2026-05-14 (review Matias PR #345 #7):
                        // quotas sao inteiros — parseInt evita que valores
                        // como 33.33 entrem no form e quebrem a comparacao
                        // exata `soma === quotasVigente`.
                        const num = parseInt(v, 10);
                        field.onChange(Number.isNaN(num) ? null : num);
                      }}
                    />
                    {fieldState.error?.message && (
                      <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                    )}
                  </FormControl>
                )}
              />
              <IconButton
                aria-label="Remover destino"
                icon={<Trash size={14} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => removerDestino(idxDestino)}
                isDisabled={acao === 'sair' && (redistribuicao ?? []).length <= 1}
              />
            </Flex>
            {motivoInvalido && (
              <Text fontSize="xs" color="red.600" mt={1}>
                {motivoInvalido}: escolha outro destino.
              </Text>
            )}
            {semDestino && (
              <Text fontSize="xs" color="red.600" mt={1}>
                Escolha quem recebe estas quotas de {quem}.
              </Text>
            )}
          </Box>
        );
      })}
    </Stack>
  );

  return (
    <Box mb={3} p={3} borderRadius="md" bg="blue.50" borderWidth={1} borderColor="blue.100">
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="sm" fontWeight="semibold" color="blue.800">
          Ação no quadro societário
        </Text>
        {acao && (
          <Badge colorScheme={ACAO_LABELS[acao].color} fontSize="2xs">
            {ACAO_LABELS[acao].label}
          </Badge>
        )}
      </Flex>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={2} mb={2}>
        {acoesOferecidas.map(opt => {
          const selecionado = acao === opt;
          // Bug 4 (smoke 2026-05-12): "Sair" exige >=1 outro socio
          // remanescente pra receber a redistribuicao. Sem destinatario,
          // o validator V_REDISTRIBUICAO_QUOTAS_BATE_VIGENTE bloqueia no
          // /gerar — preferir bloquear antes na UI.
          const isDisabled = opt === 'sair' && !selecionado && ordensDestino.length === 0;
          const btn = (
            <Button
              key={opt}
              size="sm"
              variant={selecionado ? 'solid' : 'outline'}
              colorScheme={ACAO_LABELS[opt].color}
              onClick={() => handleAcaoChange(opt)}
              flex={1}
              isDisabled={isDisabled}
            >
              {ACAO_LABELS[opt].label}
            </Button>
          );
          if (!isDisabled) return btn;
          return (
            <Tooltip
              key={opt}
              label="Sem outros sócios remanescentes pra receber as quotas. Adicione um novo sócio antes de marcar saída."
              placement="top"
              hasArrow
            >
              <Box flex={1}>{btn}</Box>
            </Tooltip>
          );
        })}
      </Stack>

      {acao && (
        <Text fontSize="xs" color="gray.600" mb={acao === 'sair' ? 3 : 0}>
          {textoDaAcao}
        </Text>
      )}

      {acao === 'alterar' && (
        <Box mt={1} p={3} borderRadius="md" bg="white" borderWidth={1} borderColor="blue.100">
          <Text fontSize="xs" fontWeight="semibold" color="blue.700" mb={2}>
            O que está sendo alterado nesse sócio?
          </Text>
          {ehPj && (
            <Text fontSize="2xs" color="gray.500" mb={2} lineHeight="1.3">
              Sócia <strong>PJ</strong>: para mudar quem administra, use o tipo{' '}
              <strong>&quot;Administração&quot;</strong>.
            </Text>
          )}
          <Controller
            control={control}
            name={`socios.${index}.tipos_alteracao_socio`}
            render={({ field, fieldState }) => (
              <FormControl isInvalid={!!fieldState.error}>
                <CheckboxGroup
                  colorScheme="blue"
                  value={field.value ?? []}
                  onChange={(next: TipoAlteracaoSocio[]) => {
                    const antes = (field.value ?? []) as TipoAlteracaoSocio[];
                    guardarOuDevolver(
                      next.filter(a => !antes.includes(a)),
                      antes.filter(a => !next.includes(a)),
                    );
                    field.onChange(next.length > 0 ? next : null);
                  }}
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={1.5}>
                    {opcoesAspecto
                      // Cada aspecto so' aparece com o tipo que o libera: fora dele o campo
                      // segue em leitura e o aspecto nao liberava nada.
                      .filter(opt => aspectoOferecido(opt.value))
                      .map(opt => (
                        <Checkbox
                          key={opt.value}
                          value={opt.value}
                          size="sm"
                          alignItems="flex-start"
                        >
                          <Box ml={1}>
                            <Text fontSize="xs" fontWeight="semibold">
                              {opt.label}
                            </Text>
                            <Text fontSize="2xs" color="gray.500" lineHeight="1.2">
                              {opt.helper}
                            </Text>
                          </Box>
                        </Checkbox>
                      ))}
                  </SimpleGrid>
                  {fieldState.error?.message && (
                    <FormErrorMessage fontSize="xs">{fieldState.error.message}</FormErrorMessage>
                  )}
                </CheckboxGroup>
              </FormControl>
            )}
          />
        </Box>
      )}

      {acao === 'sair' && (
        <Box mt={1} p={3} borderRadius="md" bg="white" borderWidth={1} borderColor="red.100">
          {/* Porta 2 Wave 1 (2026-06-18): sócio-administrador que sai mas
              permanece administrando (vira admin não-sócio). Caso dominante da
              alteração de administração — mantém tudo no v3. */}
          {eraAdministrador && (
            <Controller
              control={control}
              name={`socios.${index}.permanece_como_administrador`}
              render={({ field }) => (
                <Checkbox
                  size="sm"
                  colorScheme="purple"
                  isChecked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  mb={3}
                  alignItems="flex-start"
                >
                  <Box ml={1}>
                    <Text fontSize="xs" fontWeight="semibold">
                      Permanece como administrador (não-sócio)
                    </Text>
                    <Text fontSize="2xs" color="gray.500" lineHeight="1.2">
                      As quotas são redistribuídas (deixa de ser sócio), mas ele continua
                      administrando — assina como “Administrador”, não como retirante, e a
                      administração é ajustada no contrato.
                    </Text>
                  </Box>
                </Checkbox>
              )}
            />
          )}
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="xs" fontWeight="semibold" color="red.700">
              Redistribuição de quotas
            </Text>
            {quotasVigente != null && (
              <Text fontSize="xs" color="gray.600">
                Vigente: <strong>{quotasVigente}</strong> quotas
              </Text>
            )}
          </Flex>

          {linhasDeDestino}

          <Button
            mt={2}
            size="xs"
            leftIcon={<Plus size={12} />}
            variant="outline"
            onClick={adicionarDestino}
          >
            Adicionar destino
          </Button>

          {quotasVigente != null && (
            <Box mt={3}>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color="gray.700">
                  Total redistribuído
                </Text>
                <Text fontSize="xs" fontWeight="bold" color={somaMatch ? 'green.600' : 'red.600'}>
                  {soma} / {quotasVigente}
                </Text>
              </Flex>
              <Progress
                value={progresso}
                size="xs"
                colorScheme={somaMatch ? 'green' : 'red'}
                borderRadius="full"
              />
              {!somaMatch && (
                <Text fontSize="2xs" color="red.600" mt={1}>
                  {(!erroNaLinha && avisoExcesso) ||
                    `A soma deve bater com as quotas vigentes (${quotasVigente}).`}
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}

      {mostraCessao && (
        <Box mt={2} p={3} borderRadius="md" bg="white" borderWidth={1} borderColor="blue.100">
          <Flex justify="space-between" align="center" mb={1}>
            <Text fontSize="xs" fontWeight="semibold" color="blue.700">
              Cede quotas a (opcional)
            </Text>
            {quotasVigente != null && (
              <Text fontSize="xs" color="gray.600">
                Vigente: <strong>{quotasVigente}</strong> quotas
              </Text>
            )}
          </Flex>
          <Text fontSize="2xs" color="gray.500" mb={2} lineHeight="1.3">
            Diga para quem este sócio passa quotas: as de quem cede e as de quem recebe são
            recalculadas, e o contrato descreve cada cessão.
          </Text>
          {linhasDeDestino}
          {avisoExcesso && !erroNaLinha && (
            <Text fontSize="xs" fontWeight="semibold" color="red.600" mt={2}>
              {avisoExcesso}
            </Text>
          )}
          <Button
            mt={2}
            size="xs"
            leftIcon={<Plus size={12} />}
            variant="outline"
            colorScheme="blue"
            onClick={adicionarDestino}
          >
            Ceder quotas a um sócio
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Decide se o modo socios sync esta ativo: alteracao com `quadro_societario`
 * OU `administracao` nos tipos_alteracao do form. Centralizado pra evitar
 * logica duplicada entre StepSocios e AcaoSocioBlock.
 *
 * Wave 2 (2026-06-22): 'administracao' tambem ativa o modo. Sem isso, marcar
 * SO o tipo "Administracao" nao renderizava o AcaoSocioBlock (radio de acao,
 * aspecto Vinculo, checkbox "permanece como administrador") — o operador nao
 * conseguia expressar a mudanca de admin. Espelha
 * TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS (index.tsx).
 */
export const useSociosSyncAtivo = (): boolean => {
  const { control } = useFormContext<IWizardFormValues>();
  const tipoOperacao = useWatch({ control, name: 'tipo_operacao' });
  const tiposAlteracao = useWatch({ control, name: 'tipos_alteracao' });
  return (
    tipoOperacao === 'alteracao' &&
    (tiposAlteracao ?? []).some(
      t => t === 'quadro_societario' || t === 'administracao' || t === 'dados_socio',
    )
  );
};

/**
 * Index de "vigente extraido" por documento normalizado (CPF/CNPJ).
 * Recebido do contrato carregado (dados_extraidos.quadro_socios_atual).
 * StepSocios passa pro AcaoSocioBlock pra mostrar quotas vigentes na
 * barra de progresso da redistribuicao.
 */
export const matchVigenteByDoc = (
  socio: ISocioFormValues,
  vigentePorDoc: Record<string, { quotas?: number | null }>,
): number | null => {
  const doc = (socio.cpf || socio.cnpj || '').replace(/\D/g, '');
  if (!doc) return null;
  const found = vigentePorDoc[doc];
  if (!found) return null;
  return found.quotas ?? null;
};

export default AcaoSocioBlock;
